import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, arrayUnion, arrayRemove, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Spinner from '../components/Spinner';

const FollowContext = createContext();

export function FollowProvider({ children }) {
  const auth = getAuth();
  const [following, setFollowing] = useState(new Set());
  const [followers, setFollowers] = useState(new Set());
  const [followBacks, setFollowBacks] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize follow data on mount and auth changes
  useEffect(() => {
    const initializeFollowData = async (userId) => {
      try {
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          setFollowing(new Set(data?.following || []));
          setFollowers(new Set(data?.followers || []));
          
          // Calculate follow-backs
          const fb = new Set();
          data?.followers?.forEach(id => {
            if (!data.following?.includes(id)) fb.add(id);
          });
          setFollowBacks(fb);
        }
      } catch (error) {
        console.error("Error initializing follow data:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        initializeFollowData(user.uid);
      } else {
        // Clear follow data when user logs out
        setFollowing(new Set());
        setFollowers(new Set());
        setFollowBacks(new Set());
        setIsInitialized(true);
      }
    });

    return unsubscribe;
  }, []);

  // Real-time subscription to follow data
  useEffect(() => {
    if (!auth.currentUser || !isInitialized) return;

    const userRef = doc(db, 'users', auth.currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      const data = doc.data();
      setFollowing(new Set(data?.following || []));
      setFollowers(new Set(data?.followers || []));
      
      // Calculate follow-backs
      const fb = new Set();
      data?.followers?.forEach(id => {
        if (!data.following?.includes(id)) fb.add(id);
      });
      setFollowBacks(fb);
    });

    return unsubscribe;
  }, [auth.currentUser, isInitialized]);

  // Follow/unfollow action
  const toggleFollow = async (targetUserId) => {
    if (!auth.currentUser) return false;
    
    const isCurrentlyFollowing = following.has(targetUserId);
    const currentUserRef = doc(db, 'users', auth.currentUser.uid);
    const targetUserRef = doc(db, 'users', targetUserId);

    try {
      // Optimistic UI update
      setFollowing(prev => {
        const newSet = new Set(prev);
        isCurrentlyFollowing ? newSet.delete(targetUserId) : newSet.add(targetUserId);
        return newSet;
      });

      // Update follow backs if needed
      if (followers.has(targetUserId)) {
        setFollowBacks(prev => {
          const newSet = new Set(prev);
          isCurrentlyFollowing ? newSet.add(targetUserId) : newSet.delete(targetUserId);
          return newSet;
        });
      }

      // Database update
      await updateDoc(currentUserRef, {
        following: isCurrentlyFollowing 
          ? arrayRemove(targetUserId) 
          : arrayUnion(targetUserId)
      });

      await updateDoc(targetUserRef, {
        followers: isCurrentlyFollowing
          ? arrayRemove(auth.currentUser.uid)
          : arrayUnion(auth.currentUser.uid)
      });

      return true;
    } catch (error) {
      // Revert on error
      setFollowing(prev => {
        const newSet = new Set(prev);
        isCurrentlyFollowing ? newSet.add(targetUserId) : newSet.delete(targetUserId);
        return newSet;
      });
      console.error('Follow error:', error);
      return false;
    }
  };

  const value = {
    following,
    followers,
    followBacks,
    isFollowing: (userId) => following.has(userId),
    isFollower: (userId) => followers.has(userId),
    shouldFollowBack: (userId) => followBacks.has(userId),
    toggleFollow,
    isInitialized
  };

  return (
    <FollowContext.Provider value={value}>
      {isInitialized ? children : <Spinner />}
    </FollowContext.Provider>
  );
}

export function useFollow() {
  const context = useContext(FollowContext);
  if (!context) {
    throw new Error('useFollow must be used within a FollowProvider');
  }
  return context;
}